import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import moment from "moment-jalaali";
import MainPanelHeader from "../../../layouts/mainPanelHeader";
import PanelRightContent from "../../../layouts/mainPanel/PanelRightContent";
import PanelLeftContent from "../../../layouts/mainPanel/PanelLeftContent";
import WalletModal from "../../../component/global/WalletModal";
import DashHeader from "./DashHeader";
import { get } from "../../../controlers/global/ConfigRequest";
import {
    GET_USER_MONEY_COINS_UR,
    GET_USER_ORDERS,
    GET_USER_PROFILE,
    SURVEY_INVITATIONS,
    USED_DISCOUNT_LIST,
    USER_FEEDBACK_LIST_URL,
    USER_PANEL_CLUB,
} from "../../../helperData/ApiAddress";
import { ReactComponent as WalletIcon } from "../../../asset/icons/walletIcon.svg";
import { ReactComponent as CalenderIcon } from "../../../asset/icons/calenderIconGray.svg";
import "./style.scss";

const ACCOUNT_RIAL = 1;
const ACCOUNT_COIN = 2;
const PROFILE_FIELDS = [
    "first_name",
    "last_name",
    "phone_number",
    "national_number",
    "email",
    "birthday",
    "city",
    "job",
];

const asList = (payload) =>
    Array.isArray(payload) ? payload : payload?.results || [];

const asCount = (payload, list) => {
    if (typeof payload?.count === "number") return payload.count;
    if (typeof payload?.feedback_count === "number") return payload.feedback_count;
    return list.length;
};

const filledValue = (value) =>
    value !== null && value !== undefined && String(value).trim() !== "";

const profilePercent = (profile) => {
    if (!profile) return 0;
    const filled = PROFILE_FIELDS.filter((field) => filledValue(profile[field])).length;
    return Math.round((filled / PROFILE_FIELDS.length) * 100);
};

const greetingKey = () => {
    const hour = new Date().getHours();
    if (hour < 11) return "UserPanel.Overview.Greeting.Morning";
    if (hour < 15) return "UserPanel.Overview.Greeting.Noon";
    if (hour < 19) return "UserPanel.Overview.Greeting.Evening";
    return "UserPanel.Overview.Greeting.Night";
};

const displayName = (profile) => {
    const fromProfile = [profile?.first_name, profile?.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
    if (fromProfile) return fromProfile;
    return [
        sessionStorage.getItem("first_name"),
        sessionStorage.getItem("last_name"),
    ]
        .filter(Boolean)
        .join(" ")
        .trim();
};

const cardTitle = (card) =>
    card?.discount_card?.name || card?.code || "";

const cardCompany = (card) =>
    card?.discount_card?.company?.name || card?.discount_card?.company || "";

export default function Dashboard() {
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [walletModal, setWalletModal] = useState(false);
    const [activeTab, setActiveTab] = useState("1");
    const [payload, setPayload] = useState({
        messages: [],
        messageCount: 0,
        clubs: [],
        surveysPending: [],
        surveysDone: [],
        orders: [],
        usedCards: [],
        usedCount: 0,
        accounts: [],
        profile: {},
    });

    const formatCount = (value) =>
        Number(value || 0).toLocaleString(i18n.language === "fa" ? "fa-IR" : "en-US");

    useEffect(() => {
        let cancelled = false;
        const load = (request, fallback) => request.catch(() => fallback);

        Promise.all([
            load(get(USER_FEEDBACK_LIST_URL, { page: 1 }), { results: [], count: 0 }),
            load(get(USER_PANEL_CLUB), []),
            load(get(SURVEY_INVITATIONS, { scope: "pending" }), []),
            load(get(SURVEY_INVITATIONS, { scope: "completed" }), []),
            load(get(GET_USER_ORDERS), []),
            load(get(USED_DISCOUNT_LIST, { page: 1 }), { results: [], count: 0 }),
            load(get(GET_USER_MONEY_COINS_UR), []),
            load(get(GET_USER_PROFILE), {}),
        ]).then(
            ([
                messagesRes,
                clubs,
                surveysPending,
                surveysDone,
                orders,
                usedRes,
                accounts,
                profile,
            ]) => {
                if (cancelled) return;
                const messageList = asList(messagesRes);
                const usedList = asList(usedRes);
                setPayload({
                    messages: messageList,
                    messageCount: asCount(messagesRes, messageList),
                    clubs: asList(clubs),
                    surveysPending: asList(surveysPending),
                    surveysDone: asList(surveysDone),
                    orders: asList(orders),
                    usedCards: usedList,
                    usedCount: asCount(usedRes, usedList),
                    accounts: asList(accounts),
                    profile: profile || {},
                });
                setLoading(false);
            }
        );

        return () => {
            cancelled = true;
        };
    }, []);

    const coins = useMemo(() => {
        const match = payload.accounts.find((item) => item.account_type === ACCOUNT_COIN);
        return match?.balance ?? payload.accounts[1]?.balance ?? 0;
    }, [payload.accounts]);

    const money = useMemo(() => {
        const match = payload.accounts.find((item) => item.account_type === ACCOUNT_RIAL);
        return match?.balance ?? payload.accounts[0]?.balance ?? 0;
    }, [payload.accounts]);

    const openOrders = useMemo(
        () => payload.orders.filter((order) => ![4, 5].includes(Number(order.status))),
        [payload.orders]
    );

    const stats = useMemo(
        () => ({
            messages: payload.messageCount,
            messageCoins: payload.messages.reduce(
                (sum, item) => sum + Number(item.user_coin || 0),
                0
            ),
            surveyPending: payload.surveysPending.length,
            surveyDone: payload.surveysDone.length,
            clubs: payload.clubs.length,
            ideas: payload.clubs.reduce(
                (sum, club) => sum + Number(club.ideas_count || 0),
                0
            ),
            coins,
            usedCards: payload.usedCount,
            openOrders: openOrders.length,
            profilePercent: profilePercent(payload.profile),
        }),
        [payload, coins, openOrders.length]
    );

    const attention = useMemo(() => {
        const items = [];
        if (stats.surveyPending > 0) {
            items.push({
                key: "surveys",
                to: "/dashboard/user/poll",
                title: t("UserPanel.Overview.Attention.Surveys"),
                hint: t("UserPanel.Overview.Attention.Surveys.Hint", {
                    count: stats.surveyPending,
                }),
            });
        }
        if (stats.openOrders > 0) {
            items.push({
                key: "orders",
                to: "/dashboard/user/orders",
                title: t("UserPanel.Overview.Attention.Orders"),
                hint: t("UserPanel.Overview.Attention.Orders.Hint", {
                    count: stats.openOrders,
                }),
            });
        }
        if (stats.profilePercent < 80) {
            items.push({
                key: "profile",
                to: "/dashboard/user/profile",
                title: t("UserPanel.Overview.Attention.Profile"),
                hint: t("UserPanel.Overview.Attention.Profile.Hint", {
                    percent: stats.profilePercent,
                }),
            });
        }
        return items;
    }, [stats, t]);

    const name = displayName(payload.profile);
    const greeting = t(greetingKey());
    const recentCards = payload.usedCards.slice(0, 4);
    const recentMessages = payload.messages.slice(0, 5);

    return (
        <div className="user-dashboard user-overview main_panel_box">
            <MainPanelHeader />
            <WalletModal
                showModal={walletModal}
                closeModal={() => setWalletModal(false)}
                money={money}
                coins={coins}
            />
            <div className="overview-hero">
                <h1>
                    {name
                        ? t("UserPanel.Overview.Greeting.Named", {
                              greeting,
                              name,
                          })
                        : greeting}
                </h1>
                <div className="overview-hero__pulse">
                    <button type="button" onClick={() => setWalletModal(true)}>
                        <WalletIcon />
                        <span>
                            {t("UserPanel.Overview.Wallet")}
                            <strong>
                                {formatCount(money)} {t("Amount.Unit")}
                            </strong>
                        </span>
                    </button>
                    <Link to="/dashboard/user/discount">
                        <span>
                            {t("UserPanel.Overview.Coins")}
                            <strong>{formatCount(coins)}</strong>
                        </span>
                    </Link>
                </div>
            </div>
            <div className="dash-header">
                <DashHeader
                    stats={stats}
                    loading={loading}
                    formatCount={formatCount}
                />
            </div>
            <div className="user-dashboard-content main_panel_content">
                <PanelRightContent>
                    <div className="tab-section overview-main">
                        <Tabs
                            activeKey={activeTab}
                            transition={false}
                            className="panel_tab"
                            onSelect={(key) => setActiveTab(key)}
                        >
                            <Tab
                                eventKey="1"
                                className="panel_tab_item"
                                title={t("UserPanel.dashboard.LatestMessagesShowcase.Label")}
                            >
                                <div className="section-toolbar">
                                    <Link to="/dashboard/messages/user">
                                        {t(
                                            "UserPanel.dashboard.FeedbackHistoyShowcase.ShowAll.Button"
                                        )}
                                    </Link>
                                </div>
                                {loading ? (
                                    <div className="user-panel-empty">—</div>
                                ) : recentMessages.length === 0 ? (
                                    <div className="user-panel-empty">
                                        {t("UserPanel.Overview.Message.Empty")}
                                    </div>
                                ) : (
                                    <div className="recent-messages">
                                        {recentMessages.map((message) => (
                                            <Link
                                                key={message.id}
                                                to="/dashboard/messages/user"
                                                className="recent-message"
                                            >
                                                <div>
                                                    <strong>
                                                        {message.company ||
                                                            message.item?.name ||
                                                            t("Messages.label")}
                                                    </strong>
                                                    <span>
                                                        {message.description ||
                                                            message.item?.name ||
                                                            "—"}
                                                    </span>
                                                </div>
                                                <div className="recent-message__meta">
                                                    <span>
                                                        <CalenderIcon />
                                                        {message.datetime
                                                            ? moment(message.datetime).format(
                                                                  "jYYYY/jMM/jDD"
                                                              )
                                                            : "—"}
                                                    </span>
                                                    <b>
                                                        {formatCount(message.user_coin)}{" "}
                                                        {t("UserPanel.Overview.Coins")}
                                                    </b>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </Tab>
                            <Tab
                                eventKey="2"
                                className="panel_tab_item"
                                title={t("UserPanel.Overview.Tab.Attention")}
                            >
                                {loading ? (
                                    <div className="user-panel-empty">—</div>
                                ) : attention.length === 0 ? (
                                    <div className="user-panel-empty">
                                        {t("UserPanel.Overview.Attention.Empty")}
                                    </div>
                                ) : (
                                    <div className="attention-list">
                                        {attention.map((item) => (
                                            <Link
                                                key={item.key}
                                                to={item.to}
                                                className="attention-row"
                                            >
                                                <div>
                                                    <strong>{item.title}</strong>
                                                    <span>{item.hint}</span>
                                                </div>
                                                <em>{t("UserPanel.Overview.Open")}</em>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </Tab>
                        </Tabs>
                    </div>
                </PanelRightContent>
                <PanelLeftContent>
                    <div className="left-section">
                        <div className="chart-title">
                            <WalletIcon />
                            <div>{t("UserPanel.dashboard.Wallet.Label")}</div>
                        </div>
                        <div className="wallet">
                            <div>{t("UserPanel.dashboard.WalletBalance.Label")}</div>
                            <div>
                                {t("Amount.Unit")}
                                <span>{loading ? "—" : formatCount(money)}</span>
                            </div>
                            <div className="btn-group">
                                <button
                                    className="blue"
                                    type="button"
                                    onClick={() => setWalletModal(true)}
                                >
                                    {t("UserPanel.dashboard.WalletRecharge.Button")}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="left-section overview-recent-cards">
                        <div className="chart-title">
                            <div>{t("UserPanel.Overview.RecentCards")}</div>
                            <Link to="/dashboard/user/discount">
                                {t("UserPanel.dashboard.FeedbackHistoyShowcase.ShowAll.Button")}
                            </Link>
                        </div>
                        {loading ? (
                            <div className="user-panel-empty">—</div>
                        ) : recentCards.length === 0 ? (
                            <div className="user-panel-empty">
                                {t("UserPanel.Overview.RecentCards.Empty")}
                            </div>
                        ) : (
                            <div className="recent-card-list">
                                {recentCards.map((card) => (
                                    <Link
                                        key={card.id}
                                        to="/dashboard/user/discount"
                                        className="recent-card-row"
                                    >
                                        <div>
                                            <strong>{cardTitle(card) || t("Offers")}</strong>
                                            <span>
                                                {cardCompany(card) ||
                                                    t("UserPanel.Orders.Table.Company")}
                                            </span>
                                        </div>
                                        <em>
                                            {card.created_at
                                                ? moment(card.created_at).format(
                                                      "jYYYY/jMM/jDD"
                                                  )
                                                : "—"}
                                        </em>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </PanelLeftContent>
            </div>
        </div>
    );
}
